import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiShieldCheck, HiArrowRight } from "react-icons/hi";

import LanguageSelector from "../../atoms/LanguageSelector";
import { CustomButton } from "../../atoms/CustomButton";

const VerificationLookupPage: React.FC = () => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();

  const [entityId, setEntityId] = useState("");
  const [userId, setUserId] = useState("");
  const [requestId, setRequestId] = useState("");

  const lookupFieldClass =
    "w-full border border-gray-300 rounded-md p-2.5 md:p-2 " +
    "text-base md:text-sm focus:ring-blue-500 focus:border-blue-500";

  const stripWhitespace = (v: string) => v.replace(/\s+/g, "");

  const canSubmit = entityId !== "" && userId !== "" && requestId !== "";

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const encodedEntityId = encodeURIComponent(entityId);
    const encodedUserId = encodeURIComponent(userId);
    const encodedRequestId = encodeURIComponent(requestId);
    navigate(
      `/sign/verify/${encodedEntityId}/${encodedUserId}/${encodedRequestId}`
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 p-4">
      <div className="relative max-w-md w-full bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-8">
        <div className="absolute top-3 end-3 rounded-full bg-white ring-1 ring-gray-200 shadow-sm p-0.5">
          <LanguageSelector variant="compact" />
        </div>

        <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
          <HiShieldCheck className="text-green-500" size={36} />
        </div>

        <h1 className="text-xl font-semibold text-gray-800 text-center mb-2">
          {t("verificationLookup.title")}
        </h1>
        <p className="text-sm text-gray-500 text-center leading-relaxed mb-6">
          {t("verificationLookup.subtitle")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="requestId"
              className="block text-xs uppercase tracking-wide text-gray-500 font-medium mb-1"
            >
              {t("verificationLookup.fields.requestId")}
            </label>
            <input
              id="requestId"
              type="text"
              value={requestId}
              onChange={(e) => setRequestId(stripWhitespace(e.target.value))}
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              className={lookupFieldClass}
              placeholder={
                t("verificationLookup.fields.requestIdPlaceholder") as string
              }
            />
          </div>

          <div>
            <label
              htmlFor="entityId"
              className="block text-xs uppercase tracking-wide text-gray-500 font-medium mb-1"
            >
              {t("verificationLookup.fields.entityId")}
            </label>
            <input
              id="entityId"
              type="text"
              value={entityId}
              onChange={(e) => setEntityId(stripWhitespace(e.target.value))}
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              className={lookupFieldClass}
              placeholder={
                t("verificationLookup.fields.entityIdPlaceholder") as string
              }
            />
          </div>

          <div>
            <label
              htmlFor="userId"
              className="block text-xs uppercase tracking-wide text-gray-500 font-medium mb-1"
            >
              {t("verificationLookup.fields.userId")}
            </label>
            <input
              id="userId"
              type="text"
              value={userId}
              onChange={(e) => setUserId(stripWhitespace(e.target.value))}
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              className={lookupFieldClass}
              placeholder={
                t("verificationLookup.fields.userIdPlaceholder") as string
              }
            />
          </div>

          <CustomButton
            type="submit"
            variant="custom"
            className="w-full bg-green-600 font-medium text-white enabled:hover:bg-green-700 focus-visible:ring-green-500"
            disabled={!canSubmit}
            title={
              <>
                {t("verificationLookup.submit")}
                <HiArrowRight size={16} className="ms-2 rtl:rotate-180" />
              </>
            }
          />
        </form>

        <p className="text-xs text-gray-400 text-center mt-6 leading-relaxed">
          {t("verificationLookup.hint")}
        </p>
      </div>
    </div>
  );
};

export default VerificationLookupPage;
